import React, { useEffect } from 'react';
import { Icons } from '@/utils/Icons';
import { useAppDispatch, useAppSelector, useLockBodyScroll } from '@/hooks';
import { toggleEditStake } from '@/store';
import { useForm, useFieldArray } from 'react-hook-form';
import { StakeOption, useGetStakesQuery, useUpdateStakesMutation } from '@/store/api/betApi';
import { isGuestUser } from '@/utils';

interface StakeForm {
  chips: StakeOption[];
}

const SetButtonValueModal = () => {
  const { editStakeOpen } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();

  useLockBodyScroll(editStakeOpen);

  /* ---------------- API ---------------- */
  const isGuest = isGuestUser();
  const { data: stakeData, isFetching } = useGetStakesQuery(undefined, { skip: isGuest });
  const [updateStakes, { isLoading: isUpdating }] = useUpdateStakesMutation();

  /* ---------------- FORM ---------------- */
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StakeForm>({
    defaultValues: { chips: [] },
  });

  const { fields } = useFieldArray({
    control,
    name: 'chips',
  });

  /* ---------------- Populate Data ---------------- */
  useEffect(() => {
    if (stakeData?.stakeSet) {
      reset({ chips: stakeData.stakeSet });
    }
  }, [stakeData, reset]);

  /* ---------------- Reset on Close ---------------- */
  useEffect(() => {
    if (!editStakeOpen) {
      reset();
    }
  }, [editStakeOpen, reset]);

  /* ---------------- Submit ---------------- */
  const onSubmit = async (data: StakeForm) => {
    try {
      await updateStakes({ data: data.chips }).unwrap();
      dispatch(toggleEditStake());
    } catch (err) {
      console.error(err);
    }
  };

  if (!editStakeOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex justify-center bg-black/60">
      <div
        className="w-full max-w-[500px] h-[629px] bg-white flex flex-col overflow-hidden 
transform transition-all duration-300 ease-out animate-slide-in-from-top"
      >
        {/* HEADER */}
        <div className="bg-primary text-white p-[10px] flex justify-between items-center">
          <h2 className="font-bold text-[20px]">Set Button Value</h2>

          <button onClick={() => dispatch(toggleEditStake())}>
            <Icons.Cross size={20} />
          </button>
        </div>

        <div className="max-h-[calc(100vh-50px)] overflow-y-scroll">
          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 p-3">
            {/* LABEL ROW */}
            <div className="grid grid-cols-2 gap-x-5 text-base font-bold mb-2">
              <span>Price label :</span>
              <span>Price Value :</span>
            </div>

            {/* INPUT ROWS */}
            <div className="space-y-2">
              {isFetching ? (
                <div>Loading...</div>
              ) : (
                fields.map((field, i) => (
                  <div key={field.id} className="grid grid-cols-2 gap-y-3 gap-x-[20px]">
                    {/* LABEL */}
                    <div className="flex flex-col">
                      <input
                        {...register(`chips.${i}.label` as const, {
                          required: 'Label is required',
                          maxLength: {
                            value: 6,
                            message: 'Maximum 6 characters allowed',
                          },
                          validate: (value) =>
                            /^[a-zA-Z0-9]*$/.test(value) || 'Only alphanumeric characters allowed',
                          onChange: (e) => {
                            e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                          },
                        })}
                        className={`border h-[36px] font-normal p-[5px] text-base outline-none ${
                          errors?.chips?.[i]?.label
                            ? 'border-red-500'
                            : 'border-gray-300 focus:border-black'
                        }`}
                      />

                      {errors?.chips?.[i]?.label && (
                        <span className="text-red-500 text-xs mt-1">
                          {errors.chips[i]?.label?.message}
                        </span>
                      )}
                    </div>

                    {/* VALUE */}
                    <div className="flex flex-col">
                      <input
                        type="number"
                        min={1}
                        step="1"
                        inputMode="numeric"
                        {...register(`chips.${i}.value` as const, {
                          valueAsNumber: true,
                          required: 'Value is required',
                          min: {
                            value: 1,
                            message: 'Value must be greater than 0',
                          },
                        })}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`border h-[36px] font-normal p-[5px] text-base outline-none ${
                          errors?.chips?.[i]?.value
                            ? 'border-red-500'
                            : 'border-gray-300 focus:border-black'
                        }`}
                      />

                      {errors?.chips?.[i]?.value && (
                        <span className="text-red-500 text-xs mt-1">
                          {errors.chips[i]?.value?.message}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* UPDATE BUTTON */}
            <div className="mt-4">
              <button
                type="submit"
                disabled={isUpdating}
                className="px-3 py-[6px] h-[38px] text-white text-[16px] bg-primary"
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetButtonValueModal;
